import { useRouter } from 'next/navigation';

import { MouseEvent } from 'react';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { Dropdown, DropdownItem } from '@uikit';

import { OpenableApp } from '@api';

import Delete from '@uikit/icons/Delete';
import More from '@uikit/icons/More';
import Open from '@uikit/icons/Open';

import NextLink from '@shared/common/components/NextLink';
import { getAppStatus } from '@shared/helpers/app';

import AppStatusBadge from '@store:web/components/common/AppCard/AppStatusBadge';
import useAppOpenLink from '@store:web/utils/hooks/useAppLink';

interface SpacesAppCardProps {
  app: OpenableApp;
  onOpen?: () => void;
  onOpenPage?: () => void;
  onRemove?: () => void;
}

const SpacesAppCard = ({ app, onOpen, onRemove }: SpacesAppCardProps) => {
  const router = useRouter();
  const appOpenLink = useAppOpenLink(app);
  const appStatus = getAppStatus({
    productionReady: app.isProductionReady,
    status: app.validationStatus,
  });

  const handleOpenLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (typeof appOpenLink === 'object' && appOpenLink.isAvailableInSpace) {
      if (onOpen) {
        onOpen();
      }

      e.preventDefault();
    }
  };

  const openAppPage = () => {
    router.push(`/app/${app.appId}`);
  };

  return (
    <div
      className="relative select-none rounded-lg transition-colors hover:bg-neutral-50/25 cursor-pointer aspect-square"
      data-qa="spaces-app"
    >
      <NextLink
        {...(typeof appOpenLink === 'string'
          ? {
              target: '_blank',
              rel: 'noreferrer',
              href: appOpenLink,
            }
          : {
              href: appOpenLink.url,
            })}
        onClick={handleOpenLinkClick}
        className="flex w-full h-full justify-center items-center flex-col p-4 gap-2"
      >
        <AppStatusBadge hideLabel status={appStatus}>
          <ImageProxy
            className="rounded-xl w-11 h-11 md:w-12 md:h-12 min-[1600px]:w-16 min-[1600px]:h-16 flex-shrink-0"
            alt="Icon"
            src={app.icon}
            width={75}
            height={75}
            data-qa="spaces-app-icon"
          />
        </AppStatusBadge>

        <p className="text-center truncate t-body4 w-full" data-qa="spaces-app-name">
          {app.name}
        </p>
      </NextLink>

      <Dropdown
        placement="bottom-start"
        trigger={
          <button
            className={classNames(
              'absolute flex justify-center items-center top-2 right-2 p-0.5 w-5 h-5 rounded-sm',
              'transition-colors hover:bg-neutral-50/60',
            )}
            data-qa="spaces-app-more-button"
          >
            <More className="icon-[100%] pointer-events-none" />
          </button>
        }
      >
        <DropdownItem
          label="Open App Info"
          className="gap-2.5 flex items-center"
          onClick={openAppPage}
        >
          <Open className="icon-xs text-green-600" /> Open App Info
        </DropdownItem>
        <DropdownItem
          label="Remove from spaces"
          className="gap-2.5 flex items-center text-secondary"
          onClick={onRemove}
        >
          <Delete className="icon-xs" /> Remove from spaces
        </DropdownItem>
      </Dropdown>
    </div>
  );
};

export default SpacesAppCard;
